import type { BookingState, PackageType } from "@/types/booking"

/**
 * The booking selection, as it travels in the query string.
 *
 * Keeps the link short and readable — only what identifies a package, never
 * traveller details. Every field is optional: a link may carry just the search
 * form, or the whole selection down to the add-ons.
 */
export interface BookingUrlParams {
  mode?: PackageType
  from?: string[]
  pax?: number
  period?: number
  departDate?: string
  returnDate?: string
  hotel?: string
  flight?: string
}

const PACKAGE_TYPES: PackageType[] = ["flight-hotel", "hotel-only", "flight-only"]

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Turns a hotel or add-on name into the readable token that goes in the link.
 *
 * A link is something a partner pastes into an email, so "penzion-stary-most"
 * beats a UUID. Ids are still accepted when reading, so links shared before
 * this — and any link made by hand from an id — keep working.
 */
export function slugify(name: string | null | undefined): string {
  if (!name) return ""
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60)
}

/** True when the token identifies this option, by readable name or by id. */
export function matchesToken(
  token: string,
  id: string,
  name: string | null | undefined
): boolean {
  return token === id || (token !== "" && token === slugify(name))
}

function parsePositiveInt(raw: string | null, max: number): number | undefined {
  if (raw === null) return undefined
  if (!/^\d+$/.test(raw)) return undefined
  const value = Number(raw)
  if (value < 1 || value > max) return undefined
  return value
}

function parseIndex(raw: string | null): number | undefined {
  if (raw === null) return undefined
  if (!/^\d+$/.test(raw)) return undefined
  return Number(raw)
}

function parseDate(raw: string | null): string | undefined {
  if (raw === null || !ISO_DATE.test(raw)) return undefined
  return raw
}

/**
 * Reads a booking selection out of a query string.
 *
 * Anything malformed is dropped rather than thrown — a hand-edited or stale
 * link must still open the page, just without the broken part.
 */
export function parseBookingParams(search: string): BookingUrlParams {
  const params = new URLSearchParams(search)
  const result: BookingUrlParams = {}

  const mode = params.get("mode")
  if (mode !== null && (PACKAGE_TYPES as string[]).includes(mode)) {
    result.mode = mode as PackageType
  }

  const from = params.get("from")
  if (from) {
    const codes = from
      .split(",")
      .map((code) => code.trim().toUpperCase())
      .filter((code) => /^[A-Z]{3}$/.test(code))
    if (codes.length > 0) result.from = [...new Set(codes)]
  }

  const pax = parsePositiveInt(params.get("pax"), 99)
  if (pax !== undefined) result.pax = pax

  const period = parseIndex(params.get("period"))
  if (period !== undefined) result.period = period

  const departDate = parseDate(params.get("depart"))
  if (departDate !== undefined) result.departDate = departDate

  const returnDate = parseDate(params.get("return"))
  if (returnDate !== undefined) result.returnDate = returnDate

  const hotel = params.get("hotel")
  if (hotel) result.hotel = hotel

  const flight = params.get("flight")
  if (flight) result.flight = flight

  // An "addons" parameter from an older link is read by nobody and simply
  // falls away — see buildBookingSearch for why add-ons stay out of the URL.

  return result
}

/** Turns the live booking state into the query string that reproduces it. */
export function buildBookingSearch(state: BookingState): string {
  const params = new URLSearchParams()

  params.set("mode", state.packageType)

  if (state.departureAirports.length > 0) {
    params.set("from", state.departureAirports.map((a) => a.code).join(","))
  }

  if (state.passengerCount > 1) {
    params.set("pax", String(state.passengerCount))
  }

  if (state.customFlightDepartureDate && state.customFlightReturnDate) {
    params.set("depart", state.customFlightDepartureDate.slice(0, 10))
    params.set("return", state.customFlightReturnDate.slice(0, 10))
  } else if (state.selectedStayPeriodIndex !== null) {
    params.set("period", String(state.selectedStayPeriodIndex))
  }

  // Only a selection the traveller made is worth linking — an auto-picked
  // hotel or flight is whatever the offer search returns that day.
  if (state.userSelectedHotel && state.selectedHotel) {
    params.set(
      "hotel",
      slugify(state.selectedHotel.name) || state.selectedHotel.allotmentId
    )
  }

  // A flight has no name worth slugifying — it is a route and a time — so it
  // keeps its selection id.
  if (state.userSelectedFlight && state.selectedFlight) {
    params.set("flight", state.selectedFlight.selectionId)
  }

  // Add-ons deliberately stay out. A link carries the package someone put
  // together — the dates, the hotel, the flight — and the extras are picked on
  // the page anyway. Carrying them made the link long and, once an add-on
  // could be tied to a stay period, ambiguous: two periods can hold add-ons of
  // the same name, and hotel add-ons could not be restored at all.

  return params.toString()
}

/** True when the link carries enough to run the offer search on its own. */
export function canRestoreSearch(params: BookingUrlParams): boolean {
  const hasDates =
    params.period !== undefined ||
    (params.departDate !== undefined && params.returnDate !== undefined)
  return hasDates && (params.hotel !== undefined || params.flight !== undefined)
}

/**
 * Whether the booking already holds choices a link has no business
 * overwriting — results fetched, or a hotel or flight picked.
 *
 * A link is for starting from, not for re-applying to a booking already under
 * way. Two things an event sets by itself are deliberately not counted, since
 * neither says the traveller has done anything: selectedStayPeriodIndex, and
 * the add-ons — withDefaultFreeAddOns picks every free one the moment the
 * event loads, so counting them would silently stop a shared link working for
 * any event that has one.
 *
 * Coming back from checkout is still covered: by then the offers are loaded
 * and a hotel or flight is chosen.
 */
export function bookingHasProgressed(state: BookingState): boolean {
  return (
    state.offers !== null ||
    state.flightOptions.length > 0 ||
    state.selectedHotel !== null ||
    state.selectedFlight !== null
  )
}
